import { useCallback, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  computeSalesAnalyticsForRange,
  type SalesAnalytics,
  type SalesGateway,
  type SalesPositionKind,
} from '@forq/sales-data';
import { CustomizableMetrics } from '@forq/ui';
import type { ResolvedPeriod } from './period';
import { ReportActions, type SectionReport } from './report';
import { SectionShell, useAsyncData } from './shared';
import { warengruppeForSalesPosition, warengruppeLabels } from './warengruppe';

function warengruppeLabel(kind: SalesPositionKind): string {
  const group = warengruppeForSalesPosition(kind);
  return group ? warengruppeLabels[group] : 'Sonstiges';
}

// Hinweis: Die CSS-Klassen (.sales-controlling*, .sales-bar*, .sales-trend*) sind weiterhin in
// modules/sales/src/sales.css definiert und global geladen (apps/web/src/main.tsx) -- sie greifen hier.

function money(value: number): string {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}
function percent(value: number): string {
  return `${(value * 100).toLocaleString('de-DE', { maximumFractionDigits: 0 })} %`;
}

/**
 * Vertriebs-Controlling: Pipeline-Trichter, Win-Rate, Umsatz nach Kanal/Quelle, Prognose und
 * Top-Kunden. Der Zeitraum kommt jetzt vom globalen Filter des Auswertung-Hubs (kein eigener Umschalter).
 */
export function SalesControllingView({ tenantId, gateway, period, tenantName }: {
  readonly tenantId: string;
  readonly gateway: SalesGateway;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  const load = useCallback(async () => {
    const [opps, offs] = await Promise.all([
      gateway.searchOpportunities({ tenantId, stage: 'all', pageSize: 500 }),
      gateway.searchOffers({ tenantId, status: 'all', pageSize: 500 }),
    ]);
    return { opportunities: opps.items, offers: offs.items };
  }, [gateway, tenantId]);
  const { data, loading, error, reload } = useAsyncData(load);

  const analytics: SalesAnalytics | null = useMemo(
    () => (data ? computeSalesAnalyticsForRange(data.opportunities, data.offers, period) : null),
    [data, period],
  );

  const report: SectionReport | null = useMemo(() => {
    if (!analytics) return null;
    return {
      title: 'Vertrieb',
      kpis: [
        { label: 'Pipeline offen', value: money(analytics.pipelineValue) },
        { label: 'Gewichtete Pipeline', value: money(analytics.weightedPipelineValue) },
        { label: 'Gewonnen', value: money(analytics.wonValue) },
        { label: 'Deckungsbeitrag', value: money(analytics.wonMargin) },
        { label: 'Win-Rate', value: analytics.decidedCount > 0 ? percent(analytics.winRate) : '–' },
        { label: 'Ø Auftragswert', value: analytics.wonCount > 0 ? money(analytics.avgDealSize) : '–' },
        { label: 'Offene Angebote', value: money(analytics.openOfferValue) },
        { label: 'Prognose Quartal (Run-Rate)', value: money(analytics.forecast.runRateQuarter) },
      ],
      blocks: [
        { heading: 'Pipeline-Trichter', columns: ['Phase', 'Anzahl', 'Erwarteter Wert'], rows: analytics.funnel.map((s) => [s.label, s.count, Math.round(s.expectedValue)]) },
        { heading: 'Umsatz nach Kanal', columns: ['Kanal', 'Gewonnene Angebote', 'Umsatz netto'], rows: analytics.byChannel.map((p) => [p.label, p.wonCount, Math.round(p.wonValue)]) },
        { heading: 'Umsatz nach Quelle', columns: ['Quelle', 'Gewonnene Angebote', 'Umsatz netto'], rows: analytics.bySource.map((p) => [p.label, p.wonCount, Math.round(p.wonValue)]) },
        { heading: 'Umsatz & DB nach Warengruppe', columns: ['Warengruppe', 'Angebote', 'Umsatz netto', 'Deckungsbeitrag'], rows: analytics.byPositionKind.map((p) => [warengruppeLabel(p.kind), p.count, Math.round(p.wonValue), Math.round(p.margin)]) },
        { heading: 'Umsatz-Trend', columns: ['Monat', 'Gewonnene Angebote', 'Umsatz netto'], rows: analytics.monthly.map((p) => [p.label, p.wonCount, Math.round(p.wonValue)]) },
        { heading: 'Top-Kunden', columns: ['Kunde', 'Nr.', 'Angebote', 'Umsatz netto'], rows: analytics.topCustomers.map((c) => [c.customerName, c.customerNumber || '–', c.wonCount, Math.round(c.wonValue)]) },
      ],
    };
  }, [analytics]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {analytics && report ? (
        <section className="sales-controlling">
          <div className="sales-controlling__head">
            <div>
              <span className="sales-eyebrow">Vertriebs-Controlling · {period.label}</span>
              <p>„Umsatz" = Netto der gewonnenen Angebote im gewählten Zeitraum; die Pipeline ist immer der aktuelle Stand.</p>
            </div>
            <div className="sales-controlling__actions">
              <button type="button" className="sales-secondary" onClick={reload}><RefreshCw size={15} /> Aktualisieren</button>
              <ReportActions report={report} period={period} tenantName={tenantName} />
            </div>
          </div>

          <div className="sales-controlling__kpis">
            <CustomizableMetrics
              pageKey="auswertung-vertrieb"
              tiles={[
                { key: 'pipeline', node: <article><span>Pipeline offen</span><strong>{money(analytics.pipelineValue)}</strong><small>{analytics.openOpportunityCount} Chancen · gewichtet {money(analytics.weightedPipelineValue)}</small></article> },
                { key: 'won', node: <article><span>Gewonnen</span><strong>{money(analytics.wonValue)}</strong><small>{analytics.wonCount} Angebote · DB {money(analytics.wonMargin)}</small></article> },
                { key: 'winrate', node: <article><span>Win-Rate</span><strong>{analytics.decidedCount > 0 ? percent(analytics.winRate) : '–'}</strong><small>{analytics.wonCount} gewonnen / {analytics.lostCount} verloren</small></article> },
                { key: 'avgdeal', node: <article><span>Ø Auftragswert</span><strong>{analytics.wonCount > 0 ? money(analytics.avgDealSize) : '–'}</strong><small>über gewonnene Angebote</small></article> },
                { key: 'openoffers', node: <article><span>Offene Angebote</span><strong>{money(analytics.openOfferValue)}</strong><small>{analytics.openOfferCount} Stück</small></article> },
              ]}
            />
          </div>

          <section className="sales-controlling__panel sales-controlling__forecast">
            <h3>Prognose</h3>
            <div className="sales-controlling__forecast-grid">
              <article><span>Gewichtete Pipeline</span><strong>{money(analytics.forecast.weightedPipeline)}</strong><small>Erwartungswert der offenen Chancen</small></article>
              <article><span>Run-Rate / Monat</span><strong>{money(analytics.forecast.runRateMonthly)}</strong><small>Ø der letzten 3 Monate</small></article>
              <article><span>Prognose Quartal</span><strong>{money(analytics.forecast.runRateQuarter)}</strong><small>Run-Rate × 3</small></article>
            </div>
            <p className="sales-controlling__hint">Vorsichtige Vorschau, keine Zusage: gewichtete Pipeline = Erwartungswert (Wert × Wahrscheinlichkeit), Run-Rate = Schnitt der zuletzt gewonnenen Umsätze.</p>
          </section>

          <div className="sales-controlling__grid">
            <section className="sales-controlling__panel">
              <h3>Pipeline-Trichter</h3>
              {analytics.funnel.every((stage) => stage.count === 0) ? (
                <p className="sales-controlling__empty">Keine offenen Verkaufschancen.</p>
              ) : analytics.funnel.map((stage) => (
                <div className="sales-bar" key={stage.stage}>
                  <span className="sales-bar__label">{stage.label}<em>{stage.count}</em></span>
                  <span className="sales-bar__track"><span className="sales-bar__fill" style={{ width: `${Math.round((stage.expectedValue / Math.max(1, ...analytics.funnel.map((s) => s.expectedValue))) * 100)}%` }} /></span>
                  <span className="sales-bar__value">{money(stage.expectedValue)}</span>
                </div>
              ))}
            </section>

            <section className="sales-controlling__panel">
              <h3>Umsatz nach Kanal</h3>
              {analytics.byChannel.length === 0 ? (
                <p className="sales-controlling__empty">Noch keine gewonnenen Angebote.</p>
              ) : analytics.byChannel.map((point) => (
                <div className="sales-bar" key={point.channel}>
                  <span className="sales-bar__label">{point.label}<em>{point.wonCount}</em></span>
                  <span className="sales-bar__track"><span className="sales-bar__fill" style={{ width: `${Math.round((point.wonValue / Math.max(1, ...analytics.byChannel.map((p) => p.wonValue))) * 100)}%` }} /></span>
                  <span className="sales-bar__value">{money(point.wonValue)}</span>
                </div>
              ))}
            </section>
          </div>

          <section className="sales-controlling__panel">
            <h3>Umsatz nach Quelle</h3>
            {analytics.bySource.length === 0 ? (
              <p className="sales-controlling__empty">Noch keine gewonnenen Angebote.</p>
            ) : analytics.bySource.map((point) => (
              <div className="sales-bar" key={point.source}>
                <span className="sales-bar__label">{point.label}<em>{point.wonCount}</em></span>
                <span className="sales-bar__track"><span className="sales-bar__fill" style={{ width: `${Math.round((point.wonValue / Math.max(1, ...analytics.bySource.map((p) => p.wonValue))) * 100)}%` }} /></span>
                <span className="sales-bar__value">{money(point.wonValue)}</span>
              </div>
            ))}
          </section>

          <section className="sales-controlling__panel">
            <h3>Umsatz &amp; Deckungsbeitrag nach Warengruppe</h3>
            {analytics.byPositionKind.length === 0 ? (
              <p className="sales-controlling__empty">Noch keine gewonnenen Angebote mit Positionen.</p>
            ) : (
              <table className="sales-controlling__table">
                <thead><tr><th>Warengruppe</th><th className="is-num">Angebote</th><th className="is-num">Umsatz netto</th><th className="is-num">Deckungsbeitrag</th></tr></thead>
                <tbody>
                  {analytics.byPositionKind.map((point) => (
                    <tr key={point.kind}>
                      <td>{warengruppeLabel(point.kind)}</td>
                      <td className="is-num">{point.count}</td>
                      <td className="is-num">{money(point.wonValue)}</td>
                      <td className="is-num">{money(point.margin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="sales-controlling__panel">
            <h3>Umsatz-Trend (gewonnen, netto)</h3>
            <div className="sales-trend" role="img" aria-label="Umsatz je Monat">
              {analytics.monthly.map((point) => (
                <div className="sales-trend__col" key={point.month} title={`${point.label}: ${money(point.wonValue)} (${point.wonCount})`}>
                  <span className="sales-trend__bar" style={{ height: `${Math.round((point.wonValue / Math.max(1, ...analytics.monthly.map((p) => p.wonValue))) * 100)}%` }} />
                  <small className="sales-trend__label">{point.label}</small>
                </div>
              ))}
            </div>
          </section>

          <section className="sales-controlling__panel">
            <h3>Top-Kunden (gewonnener Umsatz)</h3>
            {analytics.topCustomers.length === 0 ? (
              <p className="sales-controlling__empty">Noch keine gewonnenen Angebote.</p>
            ) : (
              <table className="sales-controlling__table">
                <thead><tr><th>Kunde</th><th>Nr.</th><th className="is-num">Angebote</th><th className="is-num">Umsatz netto</th></tr></thead>
                <tbody>
                  {analytics.topCustomers.map((customer) => (
                    <tr key={`${customer.customerNumber}-${customer.customerName}`}>
                      <td>
                        <span className="sales-controlling__custname">{customer.customerName}</span>
                        <span className="sales-controlling__custbar"><span style={{ width: `${Math.round((customer.wonValue / Math.max(1, ...analytics.topCustomers.map((c) => c.wonValue))) * 100)}%` }} /></span>
                      </td>
                      <td>{customer.customerNumber || '–'}</td>
                      <td className="is-num">{customer.wonCount}</td>
                      <td className="is-num">{money(customer.wonValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </section>
      ) : null}
    </SectionShell>
  );
}
